// useDialogFocusTrap.js: keyboard focus stays inside an open dialog (2026-10-14; fullstack
// claude/FORMS.md § 17 Focus). Each Tab moves the focus to the next control (Shift+Tab the
// previous), wrapping at the ends; only controls that are shown and enabled count, read at each
// key press, so a new tab's content is included as soon as it's shown. When the dialog closes, the
// focus goes back to the element that opened it, if it's still on the page.
// Used by App/Modal/Dialog when it's given traps-focus.

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=\"hidden\"])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex=\"-1\"])",
  "[contenteditable=\"true\"]",
  "iframe" // e.g. Stripe's fields: Tab enters the frame, and Stripe moves on from there
].join(",");

function getIsShown(element) {
  if (element.closest("[inert], [aria-hidden=\"true\"]")) return false;
  return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden";
}

// a group of same-named radio buttons is one stop: its checked one (else its first)
function getIsRadioStop(element, focusables) {
  if (element.type !== "radio" || !element.name) return true;
  const isSameGroup = (other) => other.type === "radio" && other.name === element.name;
  const group = focusables.filter(isSameGroup);
  return element === (group.find((radio) => radio.checked) || group[0]);
}

function getFocusables(dialog) {
  const shown = [...dialog.querySelectorAll(FOCUSABLE)].filter((element) => {
    return element.tabIndex >= 0 && getIsShown(element);
  });
  return shown.filter((element) => getIsRadioStop(element, shown));
}

function getIsAfter(active, element) {
  return Boolean(active.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
}

// the stop after (or before) the focused element, wrapping at the ends; from an element that
// isn't a stop (e.g. the scroll area), the nearest one that way in document order
function getNextStop(focusables, active, isBackwards) {
  const count = focusables.length;
  const index = focusables.indexOf(active);
  if (index >= 0) return focusables[(index + (isBackwards ? count - 1 : 1)) % count];
  const after = focusables.find((element) => getIsAfter(active, element));
  if (!isBackwards) return after || focusables[0];
  const before = focusables.filter((element) => !getIsAfter(active, element));
  return before.at(-1) || focusables[count - 1];
}

// the stop, or the next one that takes the focus: Firefox won't focus a frame from a script
// (2026-10-17: wrapping from ✕ to Stripe's fields left the focus on ✕ at every Tab)
function focusStop(focusables, stop, isBackwards) {
  let candidate = stop;
  for (let tries = 0; tries < focusables.length; tries++) {
    // a keyboard move: its ring shows (Safari, after a click)
    candidate.focus({focusVisible: true});
    if (document.activeElement === candidate || candidate.contains(document.activeElement)) return;
    candidate = getNextStop(focusables, candidate, isBackwards);
  }
}

// every Tab moves the focus here, not only at the ends (2026-10-16): Safari's own Tab skips buttons
// and links unless a setting is on, so from ✕ it left the dialog for the page; now each browser
// visits the same controls, and the focus never leaves
function onTab(event, dialog) {
  const focusables = getFocusables(dialog);
  if (!focusables.length) return;
  const active = document.activeElement;
  const isInside = dialog.contains(active) && active !== dialog;
  const next = isInside ? getNextStop(focusables, active, event.shiftKey)
    : event.shiftKey ? focusables.at(-1) : focusables[0];
  // a frame backwards: the browser enters it at its last field (we'd focus the frame itself)
  if (isInside && event.shiftKey && next.tagName === "IFRAME") return;
  event.preventDefault();
  focusStop(focusables, next, event.shiftKey);
}

// dialogRef: the <dialog>; returns {rememberOpener, restoreFocus} for its open and close
export function useDialogFocusTrap(dialogRef) {
  let opener = null;
  function onKeydown(event) {
    if (event.key !== "Tab" || !dialogRef.value?.open) return;
    onTab(event, dialogRef.value);
  }
  function rememberOpener() {
    const active = document.activeElement;
    opener = active && active !== document.body ? active : null;
  }
  function restoreFocus() {
    const element = opener;
    opener = null;
    if (!element?.isConnected || !getIsShown(element)) return;
    requestAnimationFrame(() => element.focus());
  }
  // Tab out of a frame (e.g. Stripe's fields): the key is pressed inside the frame, so onKeydown
  // never sees it, and the browser may leave the focus on the page itself (2026-10-16); then it
  // goes to the control after the frame
  function onFocusOut(event) {
    const dialog = dialogRef.value;
    const frame = event.target;
    if (!dialog?.open || frame?.tagName !== "IFRAME") return;
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body && dialog.contains(active)) return;
      const focusables = getFocusables(dialog);
      if (focusables.length) getNextStop(focusables, frame, false).focus({focusVisible: true});
    });
  }
  return {onKeydown, onFocusOut, rememberOpener, restoreFocus};
}
